import { useState, useEffect, useRef, useCallback } from 'react';

const MOBILE_COARSE_QUERY = '(max-width: 1023px) and (pointer: coarse)';

function readTooltipTranslate(root) {
  const wrap = root instanceof Element ? root.querySelector('.recharts-tooltip-wrapper') : null;
  if (!wrap) return null;
  const tr = wrap.style.transform || '';
  const m = /translate3d\(([-\d.]+)px,\s*([-\d.]+)px/.exec(tr)
    || /translate\(([-\d.]+)px,\s*([-\d.]+)px\)/.exec(tr);
  if (m) return { x: Number(m[1]), y: Number(m[2]) };
  const left = parseFloat(wrap.style.left);
  const top = parseFloat(wrap.style.top);
  if (Number.isFinite(left) && Number.isFinite(top)) return { x: left, y: top };
  return null;
}

function getCoarsePointer() {
  if (typeof window === 'undefined') return false;
  try {
    return window.matchMedia(MOBILE_COARSE_QUERY).matches;
  } catch {
    return 'ontouchstart' in window;
  }
}

/**
 * Touch: Recharts uses hover by default — use trigger "click" on mobile coarse
 * pointers (<1024px + touch) and remount Tooltip when the user taps/scrolls
 * outside the chart to dismiss it (iOS / touch "sticky tooltip" bug).
 *
 * @param {React.RefObject<HTMLElement | null>} chartBoundaryRef — element wrapping the chart (or scroll viewport)
 * @returns {{ trigger: 'hover' | 'click', tooltipDismissKey: number }}
 */
export function useChartTooltipTrigger(chartBoundaryRef) {
  const [isCoarse, setIsCoarse] = useState(getCoarsePointer);
  const [tooltipDismissKey, setTooltipDismissKey] = useState(0);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_COARSE_QUERY);
    const onChange = () => setIsCoarse(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!isCoarse) return undefined;

    const dismiss = () => setTooltipDismissKey((k) => k + 1);

    const onPointerDown = (e) => {
      const root = chartBoundaryRef?.current;
      if (!root || root.contains(e.target)) return;
      dismiss();
    };

    // Only dismiss on scrolls that originate OUTSIDE the chart. A scroll
    // inside the chart boundary (e.g. the horizontal-scroll viewport on the
    // dual-plot charts) must not remount the chart mid-pan — that was the
    // cause of the tooltip-remount jank (B1).
    const onScroll = (e) => {
      const root = chartBoundaryRef?.current;
      if (root && e.target instanceof Node && root.contains(e.target)) return;
      dismiss();
    };

    document.addEventListener('pointerdown', onPointerDown, { capture: true });
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, { capture: true });
      document.removeEventListener('scroll', onScroll, { capture: true });
    };
  }, [isCoarse, chartBoundaryRef]);

  const [held, setHeld] = useState(false);
  const [pin, setPin] = useState(null);

  const clearPin = () => {
    setHeld(false);
    setPin(null);
  };

  useEffect(() => {
    clearPin();
  }, [tooltipDismissKey]);

  useEffect(() => {
    if (!held) return undefined;
    const onPointerDown = (e) => {
      const node = e.target instanceof Element ? e.target : e.target?.parentElement;
      if (node?.closest?.('.recharts-tooltip-wrapper')) return;
      const root = chartBoundaryRef?.current;
      if (root && node && root.contains(node)) return;
      clearPin();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [held, chartBoundaryRef]);

  const tapRef = useRef({ x: 0, y: 0, tracking: false, ignoreClick: false });
  const heldRef = useRef(false);
  heldRef.current = held;

  const markTapStart = useCallback((e) => {
    const pt = e?.changedTouches?.[0] || e?.touches?.[0] || e;
    if (pt && typeof pt.clientX === 'number') {
      tapRef.current.x = pt.clientX;
      tapRef.current.y = pt.clientY;
      tapRef.current.tracking = true;
    }
  }, []);

  const pinTooltip = useCallback((state, e) => {
    if (e?.type === 'click' && tapRef.current.ignoreClick) {
      tapRef.current.ignoreClick = false;
      return;
    }
    const tap = tapRef.current;
    if (tap.tracking) {
      const pt = e?.changedTouches?.[0] || e?.touches?.[0] || e;
      const x = typeof pt?.clientX === 'number' ? pt.clientX : tap.x;
      const y = typeof pt?.clientY === 'number' ? pt.clientY : tap.y;
      tap.tracking = false;
      const dx = x - tap.x;
      const dy = y - tap.y;
      if (dx * dx + dy * dy > 64) return;
    }
    if (heldRef.current) {
      clearPin();
      return;
    }
    const payload = state?.activePayload;
    if (!payload?.length) return;
    const box = readTooltipTranslate(chartBoundaryRef?.current);
    if (!box) return;
    if (e?.type === 'touchend') tapRef.current.ignoreClick = true;
    const row = payload[0]?.payload;
    const raw = row?.sortKey ?? row?.payload?.sortKey ?? state.activeLabel;
    const sortKey = Number(raw);
    setPin({
      x: box.x,
      y: box.y,
      payload,
      label: state.activeLabel,
      sortKey: Number.isFinite(sortKey) ? sortKey : raw,
    });
    setHeld(true);
  }, [chartBoundaryRef]);

  return {
    trigger: isCoarse ? 'click' : 'hover',
    tooltipDismissKey,
    tooltipActive: held ? true : undefined,
    tooltipPosition: held && pin ? { x: pin.x, y: pin.y } : undefined,
    tooltipSnap: held && pin ? { active: true, payload: pin.payload, label: pin.label } : null,
    tooltipCursorX: held && pin && pin.sortKey != null && pin.sortKey !== '' ? pin.sortKey : null,
    tooltipPinPayload: held && pin ? pin.payload : null,
    pinTooltip,
    markTapStart,
  };
}
